import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  getMonthDayGrid,
  getMonthWeeks,
  isPeriodInFuture,
  makeDaySelection,
  makeMonthSelection,
  makeQuarterSelection,
  makeWeekSelection,
  type BldPeriodSelection,
} from '@/lib/bld-report-periods';
import type { BldPeriodType } from '@/lib/bld-reports-history-api';

/**
 * Bộ chọn kỳ báo cáo giống trang frontend `user-agents`: ngày (lịch tháng), tuần (danh sách
 * tuần trong tháng), tháng (lưới 12 tháng), quý (4 quý). Kỳ bắt đầu sau `today` bị vô hiệu hóa.
 * Panel render tại chỗ (không portal) để nằm trong focus scope của Dialog bao ngoài.
 */

const FIELD_LABELS: Record<BldPeriodType, string> = {
  day: 'Ngày báo cáo',
  week: 'Tuần báo cáo',
  month: 'Tháng báo cáo',
  quarter: 'Quý báo cáo',
};

const WEEKDAY_HEADERS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const;

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
const QUARTERS = [1, 2, 3, 4] as const;

interface MonthView {
  year: number;
  month: number;
}

function dmyShort(dateOnly: string): string {
  return `${dateOnly.slice(8, 10)}/${dateOnly.slice(5, 7)}`;
}

function dmyFull(dateOnly: string): string {
  return `${dateOnly.slice(8, 10)}/${dateOnly.slice(5, 7)}/${dateOnly.slice(0, 4)}`;
}

/** Tháng/năm panel mở ra: tháng chứa ngày cuối kỳ đang chọn (tuần vắt tháng tính theo ngày cuối). */
function viewFromValue(value: BldPeriodSelection): MonthView {
  const anchor = value.periodType === 'week' ? value.periodEnd : value.periodStart;
  return { year: Number.parseInt(anchor.slice(0, 4), 10), month: Number.parseInt(anchor.slice(5, 7), 10) };
}

const cellBase =
  'rounded-md px-2 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const cellIdle = 'hover:bg-accent hover:text-accent-foreground';
const cellSelected = 'bg-primary text-primary-foreground hover:bg-primary/90';
const cellDisabled = 'cursor-not-allowed text-muted-foreground/40';

function NavButton({
  label,
  disabled,
  onClick,
  direction,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  direction: 'prev' | 'next';
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function BldPeriodPicker({
  value,
  onChange,
  today,
  className,
}: {
  value: BldPeriodSelection;
  onChange: (next: BldPeriodSelection) => void;
  /** Hôm nay theo giờ Việt Nam, `YYYY-MM-DD`. */
  today: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MonthView>(() => viewFromValue(value));
  const rootRef = useRef<HTMLDivElement>(null);

  const todayYear = Number.parseInt(today.slice(0, 4), 10);
  const todayMonth = Number.parseInt(today.slice(5, 7), 10);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape chỉ đóng panel, không đóng Dialog bao ngoài. Radix nghe Escape ở `document`
      // (capture) nên phải chặn sớm hơn ở `window` (capture).
      event.stopPropagation();
      close();
    };
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, close]);

  const select = (next: BldPeriodSelection) => {
    onChange(next);
    close();
  };

  const toggle = () => {
    if (!open) setView(viewFromValue(value));
    setOpen((previous) => !previous);
  };

  const periodType = value.periodType;
  const isMonthScoped = periodType === 'day' || periodType === 'week';

  const shiftMonth = (delta: number) =>
    setView((previous) => {
      const index = previous.year * 12 + (previous.month - 1) + delta;
      return { year: Math.floor(index / 12), month: (index % 12) + 1 };
    });
  const shiftYear = (delta: number) =>
    setView((previous) => ({ ...previous, year: previous.year + delta }));

  const atCurrentMonth = view.year * 12 + view.month >= todayYear * 12 + todayMonth;
  const atCurrentYear = view.year >= todayYear;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <span className="mb-1 block text-sm font-medium leading-none" aria-hidden="true">
        {FIELD_LABELS[periodType]}
      </span>
      <button
        type="button"
        aria-label={FIELD_LABELS[periodType]}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <span>{value.label}</span>
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`Chọn ${FIELD_LABELS[periodType].toLowerCase()}`}
          className="absolute left-0 top-full z-50 mt-2 w-[320px] rounded-lg border bg-background p-4 shadow-lg"
        >
          <div className="mb-3 flex items-center justify-between">
            {isMonthScoped ? (
              <>
                <NavButton direction="prev" label="Tháng trước" onClick={() => shiftMonth(-1)} />
                <span className="text-base font-semibold">{`Tháng ${view.month} / ${view.year}`}</span>
                <NavButton
                  direction="next"
                  label="Tháng sau"
                  disabled={atCurrentMonth}
                  onClick={() => shiftMonth(1)}
                />
              </>
            ) : (
              <>
                <NavButton direction="prev" label="Năm trước" onClick={() => shiftYear(-1)} />
                <span className="text-base font-semibold">{view.year}</span>
                <NavButton
                  direction="next"
                  label="Năm sau"
                  disabled={atCurrentYear}
                  onClick={() => shiftYear(1)}
                />
              </>
            )}
          </div>

          {periodType === 'day' && (
            <div>
              <div className="mb-1 grid grid-cols-7 text-center text-xs text-muted-foreground">
                {WEEKDAY_HEADERS.map((header) => (
                  <span key={header}>{header}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-1 text-center">
                {getMonthDayGrid(view.year, view.month).map((cell) => {
                  const disabled = !cell.inMonth || isPeriodInFuture(cell.date, today);
                  const selected = cell.date === value.periodStart;
                  return (
                    <button
                      key={cell.date}
                      type="button"
                      aria-label={dmyFull(cell.date)}
                      aria-pressed={selected}
                      disabled={disabled}
                      onClick={() => select(makeDaySelection(cell.date))}
                      className={cn(
                        cellBase,
                        selected ? cellSelected : disabled ? cellDisabled : cellIdle,
                      )}
                    >
                      {Number.parseInt(cell.date.slice(8, 10), 10)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {periodType === 'week' && (
            <div className="grid grid-cols-2 gap-2 text-center">
              {getMonthWeeks(view.year, view.month).map((week) => {
                const disabled = isPeriodInFuture(week.start, today);
                const selected = week.start === value.periodStart;
                const range = `${dmyShort(week.start)} - ${dmyShort(week.end)}`;
                return (
                  <button
                    key={week.start}
                    type="button"
                    aria-label={`Tuần ${week.index} (${range})`}
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => select(makeWeekSelection(week.start, week.index))}
                    className={cn(
                      cellBase,
                      'flex flex-col items-center py-3',
                      selected ? cellSelected : disabled ? cellDisabled : cellIdle,
                    )}
                  >
                    <span>{`Tuần ${week.index}`}</span>
                    <span className={cn('text-xs', selected ? '' : 'text-muted-foreground')}>
                      {`(${range})`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {periodType === 'month' && (
            <div className="grid grid-cols-3 gap-2 text-center">
              {MONTHS.map((month) => {
                const next = makeMonthSelection(view.year, month);
                const disabled = isPeriodInFuture(next.periodStart, today);
                const selected = next.periodStart === value.periodStart;
                return (
                  <button
                    key={month}
                    type="button"
                    aria-label={`Tháng ${month}`}
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => select(next)}
                    className={cn(
                      cellBase,
                      'py-4',
                      selected ? cellSelected : disabled ? cellDisabled : cellIdle,
                    )}
                  >
                    {`Tháng ${month}`}
                  </button>
                );
              })}
            </div>
          )}

          {periodType === 'quarter' && (
            <div className="grid grid-cols-2 gap-2 text-center">
              {QUARTERS.map((quarter) => {
                const next = makeQuarterSelection(view.year, quarter);
                const disabled = isPeriodInFuture(next.periodStart, today);
                const selected = next.periodStart === value.periodStart;
                const months = `Tháng ${(quarter - 1) * 3 + 1} - ${quarter * 3}`;
                return (
                  <button
                    key={quarter}
                    type="button"
                    aria-label={`Quý ${quarter} (${months})`}
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => select(next)}
                    className={cn(
                      cellBase,
                      'flex flex-col items-center py-3',
                      selected ? cellSelected : disabled ? cellDisabled : cellIdle,
                    )}
                  >
                    <span>{`Quý ${quarter}`}</span>
                    <span className={cn('text-xs', selected ? '' : 'text-muted-foreground')}>
                      {`(${months})`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
